// Global Instructions Rule Applied!
import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getDescriptor, ListContext, FilterSpec } from '../lib/listDescriptors.ts'
import { useMobileList } from '../lib/useMobileList.ts'
import { useFilterOptions } from '../lib/useFilterOptions.ts'
import { useScrollRestore } from '../lib/useScrollRestore.ts'
import { useListFund } from '../lib/useListFund.ts'
import { InfiniteList } from '../components/InfiniteList.tsx'
import { FilterBar } from '../components/FilterBar.tsx'
import { FundSelect } from '../components/FundSelect.tsx'
import { DesktopOnlyNotice } from './DesktopOnlyNotice.tsx'
import { detailPath } from '../lib/routes.ts'
import { makeMoneyFormatter } from '../lib/money.ts'
import { FilterValue } from '../../../../app_modules/global_configs/tableViewPreferences.ts'

const EMPTY_STAGES: any[] = []

/** Seed the UI selection from a descriptor's default filters (array-valued only). */
function seedSelected(defaults?: FilterValue[]): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const f of defaults ?? []) if (Array.isArray(f.value)) out[f.key] = f.value.map(String)
  return out
}

/**
 * Generic read-only browse list for a module (`/browse/:module`). Resolves the route param
 * to a list descriptor, runs the shared `useMobileList`, and renders a `<FilterBar>` over
 * the descriptor's filter specs plus the `<InfiniteList>`. The selected filters drive the
 * server query; companies seed Role=Portfolio by default. Unknown / desktop-only modules
 * fall back to the desktop notice.
 */
export function ListScreen(): React.JSX.Element {
  const { module } = useParams()
  const navigate = useNavigate()
  const descriptor = getDescriptor(module)

  // Selected filters (key → values). Reset when the module changes (same route, new param):
  // adjust-state-during-render so children never see the previous module's selection.
  const initialSelected = useMemo(() => seedSelected(descriptor?.defaultFilters), [descriptor])
  const [selected, setSelected] = useState<Record<string, string[]>>(initialSelected)
  const [selectedModule, setSelectedModule] = useState<string | undefined>(descriptor?.module)
  if (descriptor?.module !== selectedModule) {
    setSelectedModule(descriptor?.module)
    setSelected(initialSelected)
  }

  const extraFilters: FilterValue[] = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => Array.isArray(v) && v.length > 0)
        .map(([key, value]) => ({ key, value })),
    [selected]
  )

  // Fund selector state (prospects only; the primary selector). Persisted under the
  // fundraising preference row — see useListFund. `resolvedPipelineId` is the selected fund's
  // Fundraising pipeline, used to scope the Stage filter options to that pipeline.
  const { fundOptions, selectedFundId, resolvedPipelineId, onFundChange } = useListFund(descriptor)

  // Switching fund invalidates the (pipeline-scoped) stage selection, so reset filters back
  // to the descriptor's defaults when the fund changes.
  const handleFundChange = (fundId: string) => {
    onFundChange(fundId)
    setSelected(initialSelected)
  }

  // Filters can be pipeline-dependent (prospects' Stage scopes to the selected fund's
  // pipeline); prefer the descriptor's `filtersFor` when present, else the static `filters`.
  const effectiveFilters: FilterSpec[] | undefined = useMemo(
    () => (descriptor?.filtersFor ? descriptor.filtersFor(resolvedPipelineId) : descriptor?.filters),
    [descriptor, resolvedPipelineId]
  )

  // cfg pipeline stages → {id: {name, colour}} fallback for the deals stage chip (zero fetch).
  const stages = useSelector((s: any) => s?.globalConfigs?.pipeline_stages ?? EMPTY_STAGES)
  const currencies = useSelector((s: any) => s?.globalConfigs?.currencies)
  const stagesById = useMemo(() => {
    const map: Record<string, { name?: string; colour?: string }> = {}
    for (const st of stages) if (st?.id) map[String(st.id)] = { name: st.name, colour: st.colour }
    return map
  }, [stages])
  const formatMoney = useMemo(() => makeMoneyFormatter(currencies), [currencies])

  const filterOptions = useFilterOptions(effectiveFilters)

  const { rows, loading, loadingMore, error, hasMore, totalCount, loadMore, reload, cacheKey } = useMobileList(
    descriptor,
    extraFilters,
    selectedFundId
  )

  const ctx: ListContext = { stagesById, formatMoney }
  const items = descriptor ? rows.map((r) => descriptor.toRow(r, ctx)) : []

  useScrollRestore(cacheKey, items.length > 0)

  if (!descriptor) return <DesktopOnlyNotice />

  const showFund = Boolean(descriptor.pipelineType)
  const showFilters = Boolean(effectiveFilters && effectiveFilters.length > 0)

  return (
    <div style={{ padding: 12 }}>
      {showFund || showFilters ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          {showFund ? (
            <FundSelect options={fundOptions} value={selectedFundId} onChange={handleFundChange} />
          ) : (
            <span />
          )}
          {showFilters ? (
            <FilterBar
              specs={effectiveFilters as FilterSpec[]}
              options={filterOptions}
              selected={selected}
              onChange={setSelected}
            />
          ) : null}
        </div>
      ) : null}
      <InfiniteList
        items={items}
        loading={loading}
        loadingMore={loadingMore}
        error={error}
        hasMore={hasMore}
        totalCount={totalCount}
        onLoadMore={loadMore}
        onReload={reload}
        onRowClick={(it) => navigate(detailPath(descriptor.detailModule ?? descriptor.module, it.id))}
        emptyText={`No ${descriptor.label.toLowerCase()}`}
      />
    </div>
  )
}

export default ListScreen
